const AUTH_TOKEN_KEY = "pc_auth_token";
// Consent to persist the secure login token. This is DELIBERATELY separate
// from `pc_cookie_prefs.functional` (the general "cache data locally" banner):
// the session token is governed by the login dialog's consent, so declining
// the cache banner clears content caches without signing the user out.
const AUTH_CONSENT_KEY = "pc_auth_consent";
const AUTH_CONSENT_EVENT = "pc-auth-consent-changed";
function getStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function getCookieToken() {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie.split("; ").find(row => row.startsWith(`${AUTH_TOKEN_KEY}=`));
  if (!cookie) return null;
  const value = cookie.split("=").slice(1).join("=");
  return decodeURIComponent(value || "");
}
function setCookieToken(token) {
  if (typeof document === "undefined") return;
  const maxAgeSeconds = 60 * 60 * 24 * 7; // 7 days
  const parts = [`${AUTH_TOKEN_KEY}=${encodeURIComponent(token)}`, "path=/", `max-age=${maxAgeSeconds}`, "samesite=lax"];
  if (typeof window !== "undefined" && window.location?.protocol === "https:") {
    parts.push("secure");
  }
  document.cookie = parts.join("; ");
}
function clearCookieToken() {
  if (typeof document === "undefined") return;
  document.cookie = `${AUTH_TOKEN_KEY}=; path=/; max-age=0; samesite=lax`;
}

/**
 * Consent to store the login/session token, set by the login dialog (NOT the
 * general cache banner). `allowed()` also returns true when a token is already
 * persisted: its mere presence means the user previously agreed to stay signed
 * in, so pre-existing sessions keep working without an explicit consent key.
 */
export const authConsent = {
  allowed() {
    const s = getStorage();
    if (s) {
      if (s.getItem(AUTH_CONSENT_KEY) === "granted") return true;
      // Back-compat: an already-persisted token implies prior consent.
      if (s.getItem(AUTH_TOKEN_KEY)) return true;
    }
    // A JS-readable token cookie (only ever set alongside a granted
    // consent) counts too, even when localStorage is unavailable.
    return !!getCookieToken();
  },
  grant() {
    const s = getStorage();
    if (s) {
      try {
        s.setItem(AUTH_CONSENT_KEY, "granted");
      } catch {
        // Ignore write failures; in-memory session still works.
      }
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTH_CONSENT_EVENT));
    }
  },
  revoke() {
    const s = getStorage();
    if (s) {
      try {
        s.removeItem(AUTH_CONSENT_KEY);
      } catch {
        // Ignore removal failures.
      }
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTH_CONSENT_EVENT));
    }
  }
};
export function loadAuthToken() {
  // Ungated read: a stored token exists only because it was persisted under
  // a granted consent, so reading it back is always allowed. This keeps the
  // session alive when the user declines the general cache banner.
  const storage = getStorage();
  const localToken = storage?.getItem(AUTH_TOKEN_KEY) || null;
  if (localToken) return localToken;
  return getCookieToken();
}
export function persistAuthToken(token) {
  // Session token persistence is governed by the login dialog's consent,
  // never by the cache banner.
  if (!authConsent.allowed()) return false;
  let persisted = false;
  const storage = getStorage();
  if (storage) {
    storage.setItem(AUTH_TOKEN_KEY, token);
    persisted = true;
  }
  try {
    setCookieToken(token);
    persisted = true;
  } catch {
    // Ignore cookie write failures; localStorage persistence may still succeed
  }
  return persisted;
}
export function clearAuthToken() {
  const storage = getStorage();
  try {
    storage?.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // Ignore removal failures
  }
  clearCookieToken();
}
export { AUTH_TOKEN_KEY, AUTH_CONSENT_KEY, AUTH_CONSENT_EVENT };